
import { useEffect, useState } from "react";
import { useNavigate } from "react-router-dom";
import { toast } from "sonner";
import { Menu } from "lucide-react";
import { useAuth } from "@/contexts/auth";
import { usePermissions } from "@/hooks/use-permissions";
import { useIsMobile } from "@/hooks/use-mobile";
import AdminSidebar from "@/components/admin/AdminSidebar";
import AdminHeader from "@/components/admin/AdminHeader";
import { Tabs, TabsContent, TabsList, TabsTrigger } from "@/components/ui/tabs";
import NotificationSettings from "@/components/admin/settings/NotificationSettings";
import EmailSettings from "@/components/admin/settings/EmailSettings";
import GeneralSettings from "@/components/admin/settings/GeneralSettings";
import { AccountManagementSettings } from "@/components/admin/settings/AccountManagementSettings";
import { AdvancedSettings } from "@/components/admin/settings/AdvancedSettings";
import { Button } from "@/components/ui/button";

const AdminSettings = () => {
  const navigate = useNavigate();
  const { user, profile, isLoading } = useAuth();
  const { isSuperAdmin, isAdmin } = usePermissions();
  const isMobile = useIsMobile();
  const [sidebarOpen, setSidebarOpen] = useState(!isMobile);
  const [activeTab, setActiveTab] = useState("general");
  
  useEffect(() => {
    document.title = "Admin Settings - NationCard";
    
    // Double-check permissions on component mount
    if (!isLoading) {
      if (!user) {
        toast.error("Please log in to access the admin dashboard");
        navigate("/admin");
        return;
      }
      
      if (!isAdmin() && !isSuperAdmin()) {
        toast.error("You do not have permission to access the admin dashboard");
        navigate("/");
        return;
      }
    }
  }, [user, profile, isAdmin, isSuperAdmin, navigate, isLoading]);
  
  // Effect to handle sidebar state on mobile/desktop switch
  useEffect(() => {
    setSidebarOpen(!isMobile);
  }, [isMobile]);
  
  if (isLoading) {
    return <div className="flex h-screen w-full items-center justify-center">
      <div className="text-center">
        <div className="animate-spin rounded-full h-12 w-12 border-t-2 border-b-2 border-nation-blue mx-auto mb-4"></div>
        <p className="text-nation-blue">Loading admin dashboard...</p>
      </div>
    </div>;
  }
  
  // Additional check to ensure we have a user with proper permissions before rendering
  if (!user || (!isAdmin() && !isSuperAdmin())) {
    return null; // This will prevent render flashing while redirects happen
  }
  
  return (
    <div className="flex min-h-screen bg-nation-purple/10 dark:bg-gray-900/90">
      {/* Mobile sidebar toggle */}
      {isMobile && !sidebarOpen && (
        <Button
          variant="outline"
          size="icon"
          className="fixed top-4 left-4 z-40 md:hidden"
          onClick={() => setSidebarOpen(true)}
        >
          <Menu className="h-4 w-4" />
        </Button>
      )}
      
      {/* Sidebar with conditional display for mobile */}
      <div className={`${sidebarOpen ? "block" : "hidden"} ${isMobile ? "fixed inset-0 z-50 bg-black/50" : ""}`} onClick={isMobile ? () => setSidebarOpen(false) : undefined}>
        <div onClick={e => isMobile && e.stopPropagation()} className={isMobile ? "h-full w-64" : ""}>
          <AdminSidebar onClose={isMobile ? () => setSidebarOpen(false) : undefined} />
        </div>
      </div>
      
      <div className="flex-1 flex flex-col">
        <AdminHeader>
          {isMobile && (
            <Button
              variant="outline"
              size="icon"
              className="mr-2"
              onClick={() => setSidebarOpen(true)}
            >
              <Menu className="h-4 w-4" />
            </Button>
          )}
          Admin Settings
        </AdminHeader>
        
        <main className="p-2 md:p-6 flex-1 overflow-x-hidden admin-settings">
          <div className="mb-4">
            <div className="text-sm text-gray-500 mb-1">
              Admin / Settings
            </div>
          </div>
          <Tabs value={activeTab} onValueChange={setActiveTab} className="space-y-4 admin-settings-tabs">
            <div className="overflow-x-auto pb-2">
              <TabsList className={`${isMobile ? "w-full flex" : ""} bg-white dark:bg-gray-800 border border-gray-200 dark:border-gray-700`}>
                <TabsTrigger 
                  value="general" 
                  className={`${isMobile ? "flex-1 py-2 text-xs whitespace-nowrap" : ""} data-[state=active]:text-gray-900 data-[state=active]:dark:text-gray-100 data-[state=inactive]:text-gray-600 data-[state=inactive]:dark:text-gray-400`}
                >
                  General
                </TabsTrigger>
                <TabsTrigger 
                  value="email" 
                  className={`${isMobile ? "flex-1 py-2 text-xs whitespace-nowrap" : ""} data-[state=active]:text-gray-900 data-[state=active]:dark:text-gray-100 data-[state=inactive]:text-gray-600 data-[state=inactive]:dark:text-gray-400`}
                >
                  Email
                </TabsTrigger>
                <TabsTrigger 
                  value="notifications" 
                  className={`${isMobile ? "flex-1 py-2 text-xs whitespace-nowrap" : ""} data-[state=active]:text-gray-900 data-[state=active]:dark:text-gray-100 data-[state=inactive]:text-gray-600 data-[state=inactive]:dark:text-gray-400`}
                >
                  Notifications
                </TabsTrigger>
                <TabsTrigger 
                  value="account" 
                  className={`${isMobile ? "flex-1 py-2 text-xs whitespace-nowrap" : ""} data-[state=active]:text-gray-900 data-[state=active]:dark:text-gray-100 data-[state=inactive]:text-gray-600 data-[state=inactive]:dark:text-gray-400`}
                >
                  Account Mgmt
                </TabsTrigger>
                <TabsTrigger 
                  value="advanced" 
                  className={`${isMobile ? "flex-1 py-2 text-xs whitespace-nowrap" : ""} data-[state=active]:text-gray-900 data-[state=active]:dark:text-gray-100 data-[state=inactive]:text-gray-600 data-[state=inactive]:dark:text-gray-400`}
                >
                  Advanced
                </TabsTrigger>
              </TabsList>
            </div>
            
            <TabsContent value="general" className="pt-2">
              <GeneralSettings />
            </TabsContent>
            
            <TabsContent value="email" className="pt-2">
              <EmailSettings />
            </TabsContent>
            
            <TabsContent value="notifications" className="pt-2">
              <NotificationSettings />
            </TabsContent>
            
            <TabsContent value="account" className="pt-2">
              <AccountManagementSettings />
            </TabsContent>
            
            <TabsContent value="advanced" className="pt-2">
              <AdvancedSettings />
            </TabsContent>
          </Tabs>
        </main>
      </div>
    </div>
  );
};

export default AdminSettings;
